class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.swiperElement = this.querySelector('swiper-container');
    this.swiperInstance = null; // Store the actual Swiper instance
    this.swiperParams =
      JSON.parse(this.getAttribute('data-swiper-params')) || null;
    this.initTimeout = null; // Store timeout ID for cleanup
  }

  connectedCallback() {
    // Wait for DOM to be ready and elements to be rendered
    const initWithDelay = () => {
      // Check if navigation elements exist
      const hasNavElements =
        this.querySelector('.swiper-button-next') ||
        this.querySelector('.swiper-button-prev') ||
        this.querySelector('.swiper-pagination');

      if (hasNavElements || document.readyState === 'complete') {
        this.initialize();
      } else {
        // If elements don't exist yet, wait a bit more
        setTimeout(initWithDelay, 50);
      }
    };

    requestAnimationFrame(() => {
      this.initTimeout = setTimeout(initWithDelay, 100);
    });
  }

  initialize() {
    try {
      this.id = this.getAttribute('id');

      if (!this.swiperElement) {
        this.swiperElement = this.querySelector('swiper-container');
      }

      if (!this.swiperElement) {
        return;
      }

      if (!customElements.get('swiper-container')) {
        return;
      }

      if (!this.swiperParams) {
        this.defineDefaultParams();
      }

      if (!this.swiperParams) {
        return;
      }

      this.initSwiper();
    } catch (error) {
      // Silent fail - no console spam
    }
  }

  getParamsFromElement() {
    this.slidesPerView = parseFloat(this.dataset.gridColumns);
    this.slidesPerViewTablet =
      parseFloat(this.dataset.gridColumnsTablet) || this.slidesPerView;
    this.slidesPerViewMobile =
      parseFloat(this.dataset.gridColumnsMobile) || this.slidesPerViewTablet;

    this.spaceBetween = parseInt(this.dataset.gridGap);
    this.spaceBetweenTablet =
      parseInt(this.dataset.gridGapTablet) || this.spaceBetween;
    this.spaceBetweenMobile =
      parseInt(this.dataset.gridGapMobile) || this.spaceBetweenTablet;

    this.gridRows = parseInt(this.dataset.gridRows);
    this.gridRowsTablet =
      parseInt(this.dataset.gridRowsTablet) || this.gridRows;
    this.gridRowsMobile =
      parseInt(this.dataset.gridRowsMobile) || this.gridRowsTablet;
    this.effect = this.dataset.effect || '';
    this.crossfade = this.dataset.crossfade === 'true';
    this.loop = this.dataset.loop === 'true';
    this.autoPlay = this.dataset.autoplay === 'true';
    this.autoPlayDelay = parseInt(this.dataset.autoplayDelay) || 3000;
    this.paginationType = this.dataset.paginationType;
  }

  defineDefaultParams() {
    try {
      this.getParamsFromElement();
      let swiperElement = this.swiperElement;

      // Configure navigation based on pagination type
      if (this.paginationType === 'arrows') {
        this.navigationNextElement = this.querySelector(`#${this.id}-next`);
        this.navigationPrevElement = this.querySelector(`#${this.id}-prev`);
        this.scrollbarElement = this.querySelector(`#${this.id}-scrollbar`);

        // Fallback to class-based selectors if ID-based ones don't exist
        if (!this.navigationNextElement) {
          this.navigationNextElement = this.querySelector(
            '.swiper-button-next',
          );
        }
        if (!this.navigationPrevElement) {
          this.navigationPrevElement = this.querySelector(
            '.swiper-button-prev',
          );
        }
        if (!this.scrollbarElement) {
          this.scrollbarElement = this.querySelector('.swiper-scrollbar');
        }
      } else if (this.paginationType === 'dots') {
        this.paginationElement = this.querySelector(`#${this.id}-pagination`);
        if (!this.paginationElement) {
          this.paginationElement = this.querySelector('.swiper-pagination');
        }
      }
      // If paginationType is 'none', no navigation is configured

      this.defaultParams = {
        loop: this.loop,
        autoplay: {
          enabled: this.autoPlay,
          delay: this.autoPlayDelay,
        },
        slidesPerView: this.slidesPerViewMobile,
        spaceBetween: this.spaceBetweenMobile,
        slideOffsetAfter: this.spaceBetweenMobile,
        grid: {
          fill: 'row',
          rows: this.gridRowsMobile,
        },
        on: {
          init: (swiper) => {
            // Use arrow function to preserve 'this' context
            if (this.swiperElement) {
              this.swiperElement.classList.add('visible');
              this.swiperElement.classList.remove('invisible');
            }
          },
        },
        injectStyles: [
          `.swiper {
            overflow: var(--swiper_overflow);
            padding-right: var(--swiper_overflow_padding);
        }`,
        ],
        breakpoints: {
          749: {
            slidesPerView: this.slidesPerViewTablet,
            spaceBetween: this.spaceBetweenTablet,
            grid: {
              fill: 'row',
              rows: this.gridRowsTablet,
            },
          },
          1170: {
            slidesPerView: this.slidesPerView,
            spaceBetween: this.spaceBetween,
            grid: {
              fill: 'row',
              rows: this.gridRows,
            },
          },
        },
      };

      if (this.effect) {
        this.defaultParams.effect = this.effect;
        if (this.crossfade) {
          this.defaultParams.fadeEffect = {crossFade: this.crossfade};
        }
      }

      if (this.paginationType === 'dots' && this.paginationElement) {
        this.defaultParams.pagination = {
          enabled: true,
          el: this.paginationElement,
          clickable: true,
        };
      } else if (
        this.paginationType === 'arrows' &&
        this.navigationNextElement &&
        this.navigationPrevElement
      ) {
        this.defaultParams.navigation = {
          enabled: true,
          nextEl: this.navigationNextElement,
          prevEl: this.navigationPrevElement,
        };
        if (this.scrollbarElement) {
          this.defaultParams.scrollbar = {
            enabled: true,
            el: this.scrollbarElement,
            draggable: true,
          };
        }
      }
      // If paginationType is 'none' or elements don't exist, no navigation is configured

      this.swiperParams = this.defaultParams;
    } catch (error) {
      // Silent fail - no console spam
    }
  }

  initSwiper() {
    try {
      if (!this.swiperElement) return;

      let swiperParams = this.swiperParams || this.defaultParams;

      if (swiperParams) {
        Object.assign(this.swiperElement, swiperParams);
        this.swiperElement.initialize();
        if (this.swiperElement && this.swiperElement.swiper) {
          this.swiperInstance = this.swiperElement.swiper;
        }
      }
    } catch (error) {
      // Silent fail - no console spam
    }
  }

  disconnectedCallback() {
    // Clear any pending timeout
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
      this.initTimeout = null;
    }

    try {
      // Only attempt to destroy if we have a valid instance
      if (
        this.swiperInstance &&
        typeof this.swiperInstance.destroy === 'function'
      ) {
        // Use the actual Swiper instance if available
        this.swiperInstance.destroy(true, true);
      } else if (
        this.swiperElement &&
        typeof this.swiperElement.destroy === 'function'
      ) {
        // Fallback to element destroy if it exists (for older Swiper versions)
        this.swiperElement.destroy(true, true);
      }
      // For Swiper 11+ web components, we don't need to call destroy on the element
      // The element will handle its own cleanup
    } catch (error) {
      // Silent fail - no console spam
    } finally {
      // Always clean up references
      this.swiperElement = null;
      this.swiperInstance = null;
    }
  }
}

customElements.define('swiper-slider', SwiperSlider);
